import { Delete, MoreVert, Timeline } from '@mui/icons-material';
import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import CButton from '../../components/CButton';
import CDeleteConfirmationButton from '../../components/CDeleteConfirmationButton';
import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CModal from '../../components/CModal';
import CPageModule from '../../components/CPageModule';
import CPageModulePaginatedTable from '../../components/CPageModulePaginatedTable';
import CPageModulePlot from '../../components/CPageModulePlot';
import CStatefulContainer from '../../components/CStatefulContainer';
import Page from '../../components/layout/Page';
import XDDropdown from '../../components/selection/XDDropdown';
import XDDropdownItem from '../../components/selection/XDDropdownItem';
import { EUserRoles } from '../../enums/EUserRoles';
import addCustomTableColumn from '../../helpers/addCustomTableColumn';
import formatTableColumn from '../../helpers/formatTableColumn';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import type { IAugmentedBackendResponse } from '../../models/IAugmentedBackendResponse';
import type { IRequestParams } from '../../models/IRequestParams';
import type { IResponse } from '../../models/IResponse';
import type { ISortingMeta } from '../../models/ISortingMeta';
import type { TPandasTable } from '../../models/TPandasTable';
import { SvUsageTrackingServices } from '../../services/SvUsageTrackingServices';
import { updateSnackbar } from '../../store/appSlice';
import { userHasRole } from '../../utils/utils';

interface State {
  snapshot: IResponse<IAugmentedBackendResponse<TPandasTable>> & IRequestParams;
  tsOpen: boolean;
  tsFigure: any | null;
  tsTitle: string;
  modalOpen: boolean;
  recordToBeDeleted: any | null;
}

const APP_COL_INDEX = 0;
const TIMESERIES_COL_INDEX = 4;

export default function PAllAppTracking() {
  const allAppTrackingTitle = CLocalizedTextStringStatic('usageTracking.title');
  const { locale } = useAppSelector((state) => state.dashboard);
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultSorting: ISortingMeta = { sortCol: 1, ascending: 'descending' };
  const dispatch = useAppDispatch();
  const { keycloak } = useKeycloak();
  const isAdmin = userHasRole(keycloak.tokenParsed, [[EUserRoles.ADMIN]]);

  const [state, setState, getState] = useStateExtended<State>({
    snapshot: {
      state: { isLoading: true, isError: false },
      params: { sorting: defaultSorting }
    },
    tsOpen: false,
    tsFigure: null,
    tsTitle: 'Timeseries',
    modalOpen: false,
    recordToBeDeleted: null
  });

  const fetchOverallUsage = async (page: number, sorting?: ISortingMeta, search?: string) => {
    await updateStateResponseStatefulAttribute<State, typeof state.snapshot.data>(
      { snapshot: (await getState()).snapshot },
      setState,
      async () =>
        await SvUsageTrackingServices.getUsageOverView({
          page,
          sorting: sorting ?? defaultSorting,
          search
        })
    );
    updateStateAttribute<State>(
      { snapshot: { ...(await getState()).snapshot, params: { sorting, search } } },
      setState
    );
  };
  useEffect(() => {
    const params = {
      sortCol: Number.parseInt(searchParams.get('sortCol') ?? defaultSorting.sortCol.toString()),
      ascending: (searchParams.get('ascending') as unknown as ISortingMeta['ascending']) ?? 'descending'
    };

    const page = Number.parseInt(searchParams.get('page') ?? '1');
    const search = searchParams.get('search') ?? undefined;

    (async () => {
      await fetchOverallUsage(page, params, search);
    })();
  }, [locale]);

  const cols = Object.keys(state.snapshot.data?.results ?? {});
  const appKey = cols[APP_COL_INDEX];
  const tsKey = cols[TIMESERIES_COL_INDEX];

  const formattedResults: TPandasTable | undefined =
    tsKey && state.snapshot.data?.results
      ? formatTableColumn(
        state.snapshot.data.results,
        TIMESERIES_COL_INDEX,
        (item: any, rowArray: React.ReactNode[]) => {
          const candidate = rowArray?.[APP_COL_INDEX];
          const title = typeof candidate === 'string' ? candidate : tsKey;

          return (
            <div className="w-full flex items-center justify-center">
              <CButton
                propagateClick={false}
                style={{ borderWidth: 1 }}
                onClick={(e) => {
                  e.preventDefault();
                  updateStateAttribute<State>({ tsOpen: true, tsFigure: item, tsTitle: title }, setState);
                }}
              >
                <Timeline />
              </CButton>
            </div>
          );
        }
      )
      : state.snapshot.data?.results;

  const paginatedWithFormatted = state.snapshot.data
    ? { ...state.snapshot.data, results: formattedResults ?? state.snapshot.data.results }
    : undefined;

  const deleteHandler = async () => {
    const app = state.recordToBeDeleted?.[APP_COL_INDEX] as string;
    if (!app) return;

    const res = await SvUsageTrackingServices.deleteUsageTrackingAppWide(app);

    if (!res.hasError()) {
      // Re-fetch data with current params (keeps pagination, sorting, search)
      const { params } = state.snapshot;
      await fetchOverallUsage(
        Number(searchParams.get('page') ?? '1'),
        params?.sorting ?? defaultSorting,
        params?.search
      );
    }

    dispatch(
      updateSnackbar({
        message: res.getData().message,
        severity: res.hasError() ? 'error' : 'success',
        isVisible: true
      })
    );
  };

  const actionsLabel = CLocalizedTextStringStatic('usageTracking.table.columns.actions');

  return (
    <Page title={allAppTrackingTitle}>
      <CPageModule>
        <h1>{allAppTrackingTitle}</h1>
      </CPageModule>
      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CStatefulContainer submodule apiRequestState={state.snapshot.state} showContent={!!state.snapshot.data}>
          <CPageModulePaginatedTable
            state={state.snapshot.state}
            data={
              paginatedWithFormatted
                ? {
                  ...paginatedWithFormatted, // keep meta & state
                  results: isAdmin
                    ? addCustomTableColumn(paginatedWithFormatted.results, actionsLabel, (rowIndex, rowArray) => {
                      return (
                        <div className="flex justify-center">
                          <XDDropdown
                            usePortal
                            closeOnAnyClick
                            button={
                              <CButton style={{ borderWidth: 1 }}>
                                <MoreVert />
                              </CButton>
                            }
                          >
                            <div className="flex flex-col">
                              <XDDropdownItem
                                data-no-rowlink
                                className="justify-center text-[14px]"
                                onClick={() => {
                                  updateStateAttribute<State>(
                                    { modalOpen: true, recordToBeDeleted: rowArray },
                                    setState
                                  );
                                }}
                              >
                                <Delete />
                                <CLocalizedText dictKey="global.deleteButton" />
                              </XDDropdownItem>
                            </div>
                          </XDDropdown>
                        </div>
                      );
                    })
                    : paginatedWithFormatted.results
                }
                : undefined
            }
            params={state.snapshot.params}
            searchBarName={'appSearch'}
            searchBarPlaceholder={CLocalizedTextStringStatic('usageTracking.searchByApp')}
            colSpanArr={[3, 1, 1, 1, 0.8, 0.8]}
            colAlignmentArr={['left', 'right', 'right', 'right', 'center', 'center']}
            colFormatArr={[
              undefined,
              { in: 'tzTimestamp', out: 'localeDateString' },
              undefined,
              undefined,
              undefined,
              undefined
            ]}
            onChange={async (page, sorting, search) => {
              await fetchOverallUsage(page, sorting, search);
              searchParams.set('page', page.toString());
              searchParams.set('sortCol', sorting.sortCol.toString());
              searchParams.set('ascending', sorting.ascending);
              searchParams.set('search', search);
              setSearchParams(searchParams);
            }}
            rowLinks={
              appKey && state.snapshot.data?.results
                ? Object.values(state.snapshot.data.results[appKey] ?? {}).map((e) => `${e}`)
                : []
            }
          />
        </CStatefulContainer>
      </CPageModule>

      <CModal
        open={state.tsOpen}
        onClose={() => {
          updateStateAttribute<State>({ tsOpen: false, tsFigure: null }, setState);
        }}
        style={{ paddingLeft: 0, paddingRight: 0 }}
      >
        <CPageModule style={{ paddingTop: 0 }}>
          <h3>{state.tsTitle}</h3>
        </CPageModule>

        <CPageModulePlot disablePlotModal data={state.tsFigure?.data} layout={state.tsFigure?.layout} />
      </CModal>

      <CDeleteConfirmationButton
        showModal={state.modalOpen}
        onConfirm={async () => {
          await deleteHandler();
        }}
        onCancel={() => {
          updateStateAttribute<State>({ modalOpen: false, recordToBeDeleted: null }, setState);
        }}
        title={CLocalizedTextStringStatic('deleteApp.confirmationTitle')}
      />
    </Page>
  );
}
